import { EditionAction, EditionType, aggregateSessionEditionActions, generationTypeToEditionAction, getEditionTypeString, getUsedEditionActions, pinturaFeatureToEditionAction } from '../photoStudioTypes'
import { PhotoStudioHistoryEntry, PhotoStudioHistorySource } from './usePhotoStudioHistory.hook'

import { useFileAPI } from 'components/common/FileAPI'
import { UploadType } from 'components/common/FileAPI/fileUtils'
import { useSnackbar } from 'components/contexts/SnackbarService.context'
import { useUserData } from 'components/contexts/UserDataContext'
import { GenerationType } from 'constants/visualStaging/visualStagingTypes'
import { useSelfEditVisualsUploadFinished } from 'dataQueries'
import { useAIVisualsResult } from 'dataQueries/photoStudio.query'
import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { AnalyticsEvent } from 'utils/analytics'
import { dataURLToFile, revokeBlobUrl } from 'utils/image'
import { logger } from 'utils/logging'
import { getImageMetadata } from 'utils/serialization/getImageMetadata'

/**
 * Maps PhotoStudioHistorySource to EditionType for analytics
 */
const SOURCE_TO_EDITION_TYPE: Record<PhotoStudioHistorySource, EditionType> = {
  [PhotoStudioHistorySource.ORIGINAL]: EditionType.ADJUST, // Original image has no edition type
  [PhotoStudioHistorySource.VIRTUAL_STAGING]: EditionType.VIRTUAL_STAGING,
  [PhotoStudioHistorySource.MAGIC_ERASER]: EditionType.MAGIC_ERASER,
  [PhotoStudioHistorySource.DISCLAIMER]: EditionType.VIRTUAL_STAGING, // Disclaimer is applied on VS results
  [PhotoStudioHistorySource.ENHANCE]: EditionType.ENHANCE,
  [PhotoStudioHistorySource.ADJUST]: EditionType.ADJUST,
}

interface UseImageUploadProps {
  uploadEditedVisualToGallery: ReturnType<typeof useFileAPI>
  assignmentId: string
  sourceVisualFilename: string
  selectedAIFeature?: string
  selectedPinturaFeature?: string
  isFileVersioned: boolean
  setIsFileVersioned: (value: boolean) => void
  onExitStudio: (isFromSave?: boolean) => void
  logPhotoStudioEvent: (eventName: string, params?: any) => void
  currentJobId?: string | null
  virtualStagingJobId?: string | null
  createUploadUrlResolver: (isAIEdited: boolean) => any
  onAISaveComplete?: (responseData: { filename: string, successIds: Array<string> }) => void
  onManualSaveComplete?: (responseData: { filename: string }) => void
  imageGenerationSequenceNumber?: number
  virtualStagingState?: any
  propertyType?: string
  file?: File
  historyEntries?: PhotoStudioHistoryEntry[]
}

/**
 * Downloads an image from a signed URL and converts it to a File object
 * @param signedUrl - The signed URL to download from
 * @param headers - Optional headers for the request
 * @param filename - The filename for the resulting File object
 * @returns Promise<File> - The downloaded image as a File object
 */
const downloadImageAsFile = async (
  signedUrl: string,
  headers: Record<string, string> = {},
  filename: string = 'ai-result.jpg'
): Promise<File> => {
  const response = await fetch(signedUrl, {
    method: 'GET',
    headers: new Headers(headers),
  })

  if (!response.ok) {
    const error = new Error(`Failed to download image: ${response.statusText}`)
    logger.error('Failed to download AI result image', error, {
      context: {
        component: 'PhotoStudio',
        action: 'downloadImageAsFile',
        metadata: {
          status: response.status,
          statusText: response.statusText,
        }
      }
    })
    throw error
  }

  const blob = await response.blob()

  // Ensure the blob is a JPEG
  const jpegBlob = new Blob([blob], { type: 'image/jpeg' })

  return new File([jpegBlob], filename, { type: 'image/jpeg' })
}

export const useImageUpload = ({
  uploadEditedVisualToGallery,
  assignmentId,
  sourceVisualFilename,
  selectedAIFeature,
  selectedPinturaFeature,
  isFileVersioned,
  setIsFileVersioned,
  onExitStudio,
  logPhotoStudioEvent,
  currentJobId,
  virtualStagingJobId,
  createUploadUrlResolver,
  onAISaveComplete,
  onManualSaveComplete,
  imageGenerationSequenceNumber,
  virtualStagingState,
  propertyType,
  file,
  historyEntries,
}: UseImageUploadProps) => {
  const { t } = useTranslation(['photo_studio_editing'])
  const { spawnSuccessToast, spawnErrorToast } = useSnackbar()
  const { userPropertyWorkspaceId } = useUserData()
  const aiVisualsResult = useAIVisualsResult()
  const selfEditVisualUploadFinished = useSelfEditVisualsUploadFinished()

  const uploadAIToolImage = useCallback(async (tag: string, isToolsSwitch: boolean, overrideImageUrl?: string) => {
    // Determine which job ID to use based on the context
    const jobIdToUse = virtualStagingJobId || currentJobId
    const isAIEdited = true // always true for AI flows

    if (!jobIdToUse) {
      throw new Error('No job ID available for AI save')
    }

    try {
      let imageFile: File

      if (overrideImageUrl) {
        // Use the provided preview image (e.g., canvas with disclaimer)
        const response = await fetch(overrideImageUrl)
        const blob = await response.blob()
        const jpegBlob = new Blob([blob], { type: 'image/jpeg' })
        imageFile = new File([jpegBlob], sourceVisualFilename, { type: 'image/jpeg' })
      } else {
        // Step 1: Get the AI processing result (the processed image from Propershot)
        const aiResult = await aiVisualsResult.refetchWithParams(assignmentId, jobIdToUse)

        if (!aiResult.signedURL) {
          throw new Error('No AI result available')
        }

        // Step 2: Download the processed image from AI result
        imageFile = await downloadImageAsFile(
          aiResult.signedURL,
          aiResult.headers,
          sourceVisualFilename
        )
      }

      // Step 3: Upload using uploadEditedVisualToGallery with isAIEdited: true
      // Create resolver with isAIEdited: true for AI flow
      const uploadUrlResolver = createUploadUrlResolver(isAIEdited)

      await new Promise<void>((resolve, reject) => {

        uploadEditedVisualToGallery.uploadFiles([imageFile], {
          uploadUrlResolverFnc: uploadUrlResolver,
          tag,
          onSettled: async (successIds: Array<string>, errorIds: Array<string>, cancelledIds: Array<string>) => {
            if (successIds.length > 0) {
              spawnSuccessToast(t('success_message'), { timeout: 3500 })

              // Capture response data from the uploaded file
              // successIds contains the actual filenames returned from server
              const serverFilename = successIds[0] // First (and only) filename from server
              const responseData = {
                filename: serverFilename,
                successIds
              }

              // Call the callback with response data
              onAISaveComplete?.(responseData)

              // Log Photo Studio event with AI tool information - enhanced with all required data
              const trackVisualSaved = async () => {
                try {
                  const editionType = selectedAIFeature === 'VIRTUAL_STAGING' ? EditionType.VIRTUAL_STAGING :
                    selectedAIFeature === 'MAGIC_ERASER' ? EditionType.MAGIC_ERASER :
                      selectedAIFeature === 'AUTO_ENHANCEMENT' ? EditionType.ENHANCE : undefined

                  let imageMetadata: { image_dimensions?: string, image_format?: string } = {}
                  if (file) {
                    try {
                      const metadata = await getImageMetadata(file)
                      imageMetadata = {
                        image_dimensions: `${metadata.width}x${metadata.height}`,
                        image_format: file.type,
                      }
                    } catch (error) {
                      logger.warn('Image metadata extraction failed', error, {
                        context: {
                          component: 'PhotoStudio',
                          action: 'getImageMetadata',
                        }
                      })
                    }
                  }

                  // Build editionSettings for virtual staging
                  const editionSettings = editionType === EditionType.VIRTUAL_STAGING && virtualStagingState ? {
                    processingMode: virtualStagingState.processingMode,
                    roomType: virtualStagingState.roomType,
                    selectedStyle: virtualStagingState.selectedStyle,
                    generationType: virtualStagingState.generationType,
                    keepFloorUntouched: virtualStagingState.keepFloorUntouched,
                  } : undefined

                  // Derive editionAction from VS state or AI tool type
                  const currentEditionAction = editionType === EditionType.VIRTUAL_STAGING && virtualStagingState?.generationType
                    ? generationTypeToEditionAction(virtualStagingState.generationType)
                    : editionType === EditionType.MAGIC_ERASER
                      ? EditionAction.ERASE
                      : editionType === EditionType.ENHANCE
                        ? EditionAction.ENHANCE
                        : undefined

                  // Aggregate all session actions from history
                  const lastEntry = historyEntries?.[historyEntries.length - 1]
                  const sessionEditionActions = lastEntry
                    ? aggregateSessionEditionActions(historyEntries!, lastEntry)
                    : currentEditionAction ? [String(currentEditionAction)] : undefined

                  logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                    status: 'saved',
                    result: { successIds, uploadType: UploadType.STANDARD },
                    tool: selectedAIFeature,
                    isAITool: true,
                    editionType: getEditionTypeString(editionType),
                    ...(editionSettings ? { editionSettings } : {}),
                    ...imageMetadata,
                    sourceVisualFilename: sourceVisualFilename || undefined,
                    propertyType: propertyType || undefined,
                    ContentType: virtualStagingState?.roomType || undefined,
                    image_generation_sequence_number: imageGenerationSequenceNumber || undefined,
                    editionAction: currentEditionAction,
                    sessionEditionActions,
                  })
                } catch (error) {
                  // Fallback to basic tracking if enhanced tracking fails
                  logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                    status: 'saved',
                    result: { successIds, uploadType: UploadType.STANDARD },
                    tool: selectedAIFeature,
                    isAITool: true,
                  })
                }
              }
              trackVisualSaved()

              // Exit studio after AI tool save if we dont want to switch to manual tools
              if (!isToolsSwitch) {
                onExitStudio(true) // Pass true to indicate this is from save operation
              }

              resolve()
            } else if (errorIds.length > 0) {
              const error = new Error('AI save failed')
              spawnErrorToast(t('general_error', { error }))

              logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                status: 'failed',
                result: error,
                tool: selectedAIFeature,
                isAITool: true,
              })
              reject(error)
            }
          }
        })
      })
    } catch (error) {
      spawnErrorToast(t('general_error', { error }))

      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
        status: 'failed',
        result: error,
        tool: selectedAIFeature,
        isAITool: true,
      })
      throw error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [aiVisualsResult, assignmentId, currentJobId, selectedAIFeature, uploadEditedVisualToGallery, virtualStagingJobId, file, imageGenerationSequenceNumber, propertyType, virtualStagingState, sourceVisualFilename, historyEntries])

  /**
   * Uploads a manually edited photo (Adjust) to the gallery. Shared by the Pintura editor and the in-house editor
   * (FeatureFlag.PHOTO_STUDIO_PINTURA_REPLACEMENT); `getEditionAction` / `getSaveFlags` describe the edit for
   * PHOTO_STUDIO_SAVE_IMAGE and are read once the upload succeeded, as before.
   */
  const uploadManualEditFile = useCallback((
    originalFile: File,
    tag: string,
    isToolsSwitch: boolean,
    fetchImageUrl: (name: string) => Promise<string>,
    setPreviewImage: (url: string) => void,
    getEditionAction: () => string | undefined,
    getSaveFlags: () => Record<string, boolean>
  ): Promise<string | null> => {
    const isAIEdited = false // always false for manual edits

    return new Promise<string | null>((resolve, reject) => {
      // Always use the original source filename for final gallery saves
      // latestPreviewFilename should NOT be used here as it may contain temp/AI filenames
      const filename = sourceVisualFilename || originalFile.name

      // Create a new File object while preserving original properties
      const extendedDest = new File([originalFile], filename, {
        type: originalFile.type,
        lastModified: originalFile.lastModified,
      })

      // Manually add the `tag` property
      Object.defineProperty(extendedDest, 'tag', {
        value: tag,
        writable: false,
        enumerable: true,
      })

      const uploadUrlResolver = createUploadUrlResolver(isAIEdited)

      uploadEditedVisualToGallery.uploadFiles([extendedDest], {
        uploadUrlResolverFnc: uploadUrlResolver,
        onSettled: async (successIds: Array<string>, errorIds: Array<string>) => {
          if (errorIds.length > 0) {
            reject(new Error('Upload failed'))
            return
          }

          if (successIds.length === 1) {
            // Remove the file extension from the successId of uploaded image
            const cleanedSuccessId = successIds[0]?.replace(/\..*$/, '') || ''

            // Retry fetching the POST_WEB url (eventual consistency)
            const fetchWithRetry = async (retries = 5, delayMs = 500): Promise<string> => {
              for (let i = 0; i < retries; i++) {
                const url = await fetchImageUrl(cleanedSuccessId)
                if (url) return url
                await new Promise(res => setTimeout(res, delayMs))
              }
              return ''
            }

            const imageUrl = await fetchWithRetry()

            // If we got a URL, preload to avoid flicker, then set as preview
            if (imageUrl) {
              await new Promise<void>((resolve) => {
                const img = new Image()
                img.onload = () => resolve()
                img.onerror = () => resolve()
                img.src = imageUrl
              })
              setPreviewImage(imageUrl)
            }

            // Notify manual save completion with the exact filename from server (with extension)
            if (onManualSaveComplete) {
              const serverFilename = successIds[0]
              onManualSaveComplete({ filename: serverFilename })

              // Notify BE after upload is complete
              selfEditVisualUploadFinished.mutate({
                assignmentId: assignmentId.toString(),
                filename: serverFilename,
                workspaceId: userPropertyWorkspaceId,
              })
            }

            spawnSuccessToast(t(isToolsSwitch ? 'success_continue_editing_message' : 'success_message'), { timeout: 3500 })

            // Enhanced visual saved tracking for manual tools
            const trackManualVisualSaved = async () => {
              try {
                let imageMetadata: { image_dimensions?: string, image_format?: string } = {}
                if (file) {
                  try {
                    const metadata = await getImageMetadata(file)
                    imageMetadata = {
                      image_dimensions: `${metadata.width}x${metadata.height}`,
                      image_format: file.type,
                    }
                  } catch (error) {
                    logger.warn('Image metadata extraction failed', error, {
                      context: {
                        component: 'PhotoStudio',
                        action: 'getImageMetadata',
                      }
                    })
                  }
                }

                const editionAction = getEditionAction()

                // Compute session-wide actions including current Pintura changes
                const lastHistoryEntry = historyEntries?.[historyEntries.length - 1]
                const priorSessionActions = lastHistoryEntry
                  ? aggregateSessionEditionActions(historyEntries!, lastHistoryEntry)
                  : undefined
                const allSessionActions = new Set([
                  ...(priorSessionActions ?? []),
                  ...(editionAction ? String(editionAction).split(',') : [])
                ])
                const sessionEditionActions = allSessionActions.size > 0
                  ? Array.from(allSessionActions)
                  : undefined

                logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                  status: 'saved',
                  isAITool: false,
                  result: successIds,
                  editionType: getEditionTypeString(EditionType.ADJUST, selectedPinturaFeature),
                  ...imageMetadata,
                  sourceVisualFilename: sourceVisualFilename || undefined,
                  propertyType: propertyType || undefined,
                  image_generation_sequence_number: imageGenerationSequenceNumber || undefined,
                  editionAction,
                  sessionEditionActions,
                  ...getSaveFlags(),
                })
              } catch (error) {
                // Fallback to basic tracking
                logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                  status: 'saved',
                  isAITool: false,
                  result: successIds,
                  editionType: getEditionTypeString(EditionType.ADJUST, selectedPinturaFeature),
                  sourceVisualFilename: sourceVisualFilename || undefined,
                })
              }
            }
            trackManualVisualSaved()

            if (isToolsSwitch && !isFileVersioned) {
              setIsFileVersioned(true)
            }

            // If it is export flow (Photo Studio footer) and not AI/MANUAL tool switch, redirect to the gallery or property tab after a short delay with onExitStudio()
            if (!isToolsSwitch) {
              onExitStudio(true) // Pass true to indicate this is from save operation
            }
            resolve(imageUrl || null) // Return the image URL for history tracking
          } else {
            resolve(null)
          }
        },
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFileVersioned, uploadEditedVisualToGallery, file, imageGenerationSequenceNumber, propertyType, virtualStagingState, sourceVisualFilename, historyEntries])

  const uploadPinturaImage = useCallback(async (tag: string, isToolsSwitch: boolean, editorRef: any, changes: any, fetchImageUrl: (name: string) => Promise<string>, setPreviewImage: (url: string) => void): Promise<string | null> => {
    if (!editorRef.current) return null

    try {
      const imageWriterResult = await editorRef.current.editor.processImage()

      if (imageWriterResult.dest) {
        return uploadManualEditFile(
          imageWriterResult.dest,
          tag,
          isToolsSwitch,
          fetchImageUrl,
          setPreviewImage,
          // Derive editionAction from Pintura changes
          () => {
            const pinturaUsedActions = getUsedEditionActions(changes)
            return pinturaUsedActions.length > 0
              ? pinturaUsedActions.join(',')
              : pinturaFeatureToEditionAction(selectedPinturaFeature)
          },
          () => ({
            Crop: !!changes.crop,
            Rotation: !!changes.rotation,
            FlipX: !!changes.flipX,
            FlipY: !!changes.flipY,
            FineTune: !!(
              changes.colorMatrix ||
              changes.convolutionMatrix ||
              changes.gamma ||
              changes.vignette
            ),
            Filter: !!changes.colorMatrix?.['filter'],
            Annotate: !!changes.annotation?.some(
              (annotation: any) => annotation && !annotation.backgroundImage
            ),
            Sticker: !!changes.annotation?.some(
              (annotation: any) => annotation && annotation.backgroundImage
            ),
            Redact: !!changes.redaction,
            Frame: !!changes.frame,
            Resize: !!(
              changes.targetSize ||
              changes.cropAspectRatio
            ),
          })
        )
      }
      return null
    } catch (error) {
      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
        status: 'failed',
        result: error,
        isAITool: false,
      })
      throw error
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uploadManualEditFile, selectedPinturaFeature])

  /** The in-house Adjust editor's export: same upload, edit described by its document */
  const uploadAdjustImage = useCallback((
    tag: string,
    adjustedFile: File,
    editionAction: string | undefined,
    saveFlags: Record<string, boolean>,
    fetchImageUrl: (name: string) => Promise<string>,
    setPreviewImage: (url: string) => void
  ): Promise<string | null> => uploadManualEditFile(adjustedFile, tag, false, fetchImageUrl, setPreviewImage, () => editionAction, () => saveFlags), [uploadManualEditFile])

  /**
   * Upload the current version from history - this is the global save function
   * that saves exactly what the user sees in preview, regardless of tool or undo state.
   *
   * This replaces the job-based lookup approach and uses the history entry directly.
   */
  const uploadFromHistory = useCallback(async (
    tag: string,
    historyEntry: PhotoStudioHistoryEntry,
    isToolsSwitch: boolean,
    applyAiLabel?: (src: string) => Promise<string>,
    aiLabelSettings?: { labelType: string, colour: string, opacity: number, position?: { x: number, y: number } | null, sizeFraction?: number | null }
  ) => {
    // Determine if this is an AI-edited image based on the history source
    const isAIEdited = historyEntry.source !== PhotoStudioHistorySource.ADJUST
      && historyEntry.source !== PhotoStudioHistorySource.ORIGINAL
    const editionType = SOURCE_TO_EDITION_TYPE[historyEntry.source]
    const toolName = editionType // EditionType values match the tool names used in analytics

    // Get the image URL/data - we'll apply disclaimer if needed before converting to File
    let imageUrl: string

    if (historyEntry.imageData) {
      // Use the stored data URL directly
      imageUrl = historyEntry.imageData
    } else if (historyEntry.imageUrl) {
      // Fallback: use imageUrl if imageData is not available
      imageUrl = historyEntry.imageUrl
    } else {
      throw new Error('No image data available in history entry')
    }

    // Blob URLs minted by the label canvas pass - revoked once the File is built
    const intermediateBlobUrls: string[] = []

    if (applyAiLabel) {
      try {
        imageUrl = await applyAiLabel(imageUrl)
        intermediateBlobUrls.push(imageUrl)
      } catch (error) {
        logger.error('Failed to apply AI label on save - aborting save', error, {
          context: {
            component: 'PhotoStudio',
            action: 'applyAiLabelOnSave',
            metadata: {
              sourceVisualFilename,
              tag,
              labelType: aiLabelSettings?.labelType,
            }
          }
        })
        intermediateBlobUrls.forEach(revokeBlobUrl)
        throw error
      }
    }

    // Convert the image URL to a File
    let imageFile: File

    if (historyEntry.imageData && imageUrl === historyEntry.imageData) {
      // Use the stored data URL directly
      imageFile = dataURLToFile(imageUrl, sourceVisualFilename)
    } else {
      // Fetch from imageUrl (may be original or label-applied)
      const response = await fetch(imageUrl)
      const blob = await response.blob()
      const jpegBlob = new Blob([blob], { type: 'image/jpeg' })
      imageFile = new File([jpegBlob], sourceVisualFilename, { type: 'image/jpeg' })
    }

    // The canvas-pass blob URLs are copied into the File above - release the pinned JPEGs
    intermediateBlobUrls.forEach(revokeBlobUrl)

    try {
      // Create resolver with appropriate isAIEdited flag
      const uploadUrlResolver = createUploadUrlResolver(isAIEdited)

      await new Promise<void>((resolve, reject) => {
        uploadEditedVisualToGallery.uploadFiles([imageFile], {
          uploadUrlResolverFnc: uploadUrlResolver,
          tag,
          onSettled: async (successIds: Array<string>, errorIds: Array<string>, cancelledIds: Array<string>) => {
            if (successIds.length > 0) {
              spawnSuccessToast(t('success_message'), { timeout: 3500 })

              const serverFilename = successIds[0]
              const responseData = {
                filename: serverFilename,
                successIds
              }

              // Call the appropriate callback based on whether it's AI or manual
              if (isAIEdited) {
                onAISaveComplete?.(responseData)
              } else {
                onManualSaveComplete?.({ filename: serverFilename })

                // Notify BE after manual upload is complete
                selfEditVisualUploadFinished.mutate({
                  assignmentId: assignmentId.toString(),
                  filename: serverFilename,
                  workspaceId: userPropertyWorkspaceId,
                })
              }

              // Log analytics with all required data
              const trackVisualSaved = async () => {
                try {
                  let imageMetadata: { image_dimensions?: string, image_format?: string } = {}
                  if (file) {
                    try {
                      const metadata = await getImageMetadata(file)
                      imageMetadata = {
                        image_dimensions: `${metadata.width}x${metadata.height}`,
                        image_format: file.type,
                      }
                    } catch (error) {
                      logger.warn('Image metadata extraction failed', error, {
                        context: {
                          component: 'PhotoStudio',
                          action: 'getImageMetadata',
                        }
                      })
                    }
                  }

                  // We use editionSettings from HISTORY entry (snapshot at generation time)
                  // This ensures UNDO/REDO then save tracks the correct settings for the saved image based on the real history entry
                  const editionSettings = historyEntry.editionSettings

                  // Derive editionAction from current history entry
                  const currentEditionAction = editionSettings?.editionAction
                    ? String(editionSettings.editionAction)
                    : historyEntry.source === PhotoStudioHistorySource.MAGIC_ERASER
                      ? EditionAction.ERASE
                      : historyEntry.source === PhotoStudioHistorySource.VIRTUAL_STAGING && editionSettings?.generationType
                        ? generationTypeToEditionAction(editionSettings.generationType as GenerationType)
                        : undefined

                  // Aggregate all session actions from history
                  const sessionEditionActions = historyEntries?.length
                    ? aggregateSessionEditionActions(historyEntries, historyEntry)
                    : currentEditionAction ? [currentEditionAction] : undefined

                  logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                    status: 'saved',
                    result: { successIds, uploadType: UploadType.STANDARD },
                    tool: toolName,
                    isAITool: isAIEdited,
                    editionType: getEditionTypeString(editionType),
                    ...(editionSettings ? { editionSettings } : {}),
                    ...imageMetadata,
                    sourceVisualFilename: sourceVisualFilename || undefined,
                    propertyType: propertyType || undefined,
                    image_generation_sequence_number: historyEntry.imageGenerationSequenceNumber || imageGenerationSequenceNumber || undefined,
                    sessionEditionActions,
                    // What was burned into the saved image (EU AI Act compliance reporting)
                    has_ai_label: !!applyAiLabel,
                    ...(aiLabelSettings ? {
                      ai_label_type: aiLabelSettings.labelType,
                      ai_label_colour: aiLabelSettings.colour,
                      ai_label_opacity_percent: aiLabelSettings.opacity,
                      ai_label_custom_placement: !!aiLabelSettings.position,
                      ...(aiLabelSettings.position ? {
                        ai_label_position_x: Number(aiLabelSettings.position.x.toFixed(4)),
                        ai_label_position_y: Number(aiLabelSettings.position.y.toFixed(4)),
                      } : {}),
                      ...(aiLabelSettings.sizeFraction != null ? { ai_label_size_fraction: Number(aiLabelSettings.sizeFraction.toFixed(4)) } : {}),
                    } : {}),
                  })
                } catch (error) {
                  // Fallback to basic tracking if enhanced tracking fails
                  logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                    status: 'saved',
                    result: { successIds, uploadType: UploadType.STANDARD },
                    tool: toolName,
                    isAITool: isAIEdited,
                  })
                }
              }
              trackVisualSaved()

              // Exit studio after save if we don't want to switch to manual tools
              if (!isToolsSwitch) {
                onExitStudio(true)
              }

              resolve()
            } else if (errorIds.length > 0) {
              const error = new Error('Save failed')
              spawnErrorToast(t('general_error', { error }))

              logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
                status: 'failed',
                result: error,
                tool: toolName,
                isAITool: isAIEdited,
              })
              reject(error)
            }
          }
        })
      })
    } catch (error) {
      spawnErrorToast(t('general_error', { error }))

      logPhotoStudioEvent(AnalyticsEvent.PHOTO_STUDIO_SAVE_IMAGE, {
        status: 'failed',
        result: error,
        tool: toolName,
        isAITool: isAIEdited,
      })
      throw error
    }
  }, [
    uploadEditedVisualToGallery,
    createUploadUrlResolver,
    sourceVisualFilename,
    spawnSuccessToast,
    spawnErrorToast,
    t,
    onAISaveComplete,
    onManualSaveComplete,
    selfEditVisualUploadFinished,
    assignmentId,
    userPropertyWorkspaceId,
    file,
    logPhotoStudioEvent,
    propertyType,
    imageGenerationSequenceNumber,
    onExitStudio,
    historyEntries,
  ])

  return {
    uploadAIToolImage,
    uploadPinturaImage,
    uploadAdjustImage,
    uploadFromHistory,
  }
}
